import { ref, Ref, onMounted } from '@nuxtjs/composition-api';

export type PDPProduct = {
  product_id: number;
  spli_id?: number;
  spli_guid?: string;
  name: string;
  sku?: string;
  price?: number;
  price_after_cash_discount?: number;
  product_image_gallery?: Array<{ media_url?: string; type?: string }>;
  media?: string;
  [key: string]: any;
};

export type DrawerPayload = {
  product?: PDPProduct | null;
  isLoading?: boolean;
  cartItemId?: number | null;
  cartItemIsInsured?: boolean | null;
};

export interface UseMinicartDrawer {
  isEnabled: Ref<boolean>;
  setEnabled: (v: boolean) => void;
  drawerOpen: Ref<boolean>;
  drawerProduct: Ref<PDPProduct | null>;
  isDrawerLoading: Ref<boolean>;
  drawerCartItemId: Ref<number | null>;
  drawerCartItemIsInsured: Ref<boolean | null>;
  openDrawer: (payload?: DrawerPayload) => void;
  closeDrawer: () => void;
}

export const isEnabled = ref<boolean>(false);
const drawerOpen = ref<boolean>(false);
const drawerProduct = ref<PDPProduct | null>(null);
const isDrawerLoading = ref<boolean>(false);
const drawerCartItemId = ref<number | null>(null);
const drawerCartItemIsInsured = ref<boolean | null>(null);

function setEnabled(v: boolean) {
  isEnabled.value = Boolean(v);
}

const openDrawer = (payload: DrawerPayload = {}): void => {
  if (!isEnabled.value) return;

  const {
    product,
    isLoading,
    cartItemId,
    cartItemIsInsured
  } = payload;

  isDrawerLoading.value = Boolean(isLoading);

  if ('product' in payload) {
    drawerProduct.value = product ?? null;
  }
  if ('cartItemId' in payload) {
    drawerCartItemId.value = cartItemId ?? null;
  }
  if ('cartItemIsInsured' in payload) {
    drawerCartItemIsInsured.value = cartItemIsInsured ?? null;
  }

  drawerOpen.value = true;
};

function closeDrawer(): void {
  drawerOpen.value = false;
  drawerProduct.value = null;
  isDrawerLoading.value = false;
  drawerCartItemId.value = null;
  drawerCartItemIsInsured.value = null;
}

export default function useMinicartDrawer(): UseMinicartDrawer {
  onMounted(() => {
    if (
      process.client &&
      typeof (window as any).__MINICART_DRAWER_ENABLED === 'boolean'
    ) {
      setEnabled((window as any).__MINICART_DRAWER_ENABLED);
    }

    // Event API for external scripts/integrations
    window.addEventListener('minicartDrawer:setEnabled', (e: Event) =>
      setEnabled(Boolean((e as CustomEvent)?.detail?.enabled))
    );
    window.addEventListener('minicartDrawer:open', (e: Event) =>
      openDrawer(((e as CustomEvent)?.detail ?? {}) as DrawerPayload)
    );
    window.addEventListener('minicartDrawer:close', () => closeDrawer());

    // Tiny imperative API (for quick debugging / console use)
    (window as any).__minicartDrawer = {
      enable: () => setEnabled(true),
      disable: () => setEnabled(false),
      open: (payload?: DrawerPayload) => openDrawer(payload),
      close: () => closeDrawer()
    };
  });

  return {
    drawerOpen,
    drawerProduct,
    isDrawerLoading,
    drawerCartItemId,
    drawerCartItemIsInsured,
    isEnabled,
    closeDrawer,
    openDrawer,
    setEnabled
  };
}
